import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { operationsApi } from '@/features/workspace/api';
import type { OperationsNotification } from '@/features/operations/types';

export const OperationsNotificationsView: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<OperationsNotification[]>([]);
  const [settings, setSettings] = useState({
    email_mirror_enabled: true,
    quiet_hours: '22:00–08:00',
    delivery_mode: 'Мгновенные уведомления',
    subscribed_tags: ['#scopus', '#linguistics', '#grant', '#conference', '#science'],
  });
  const [activeType, setActiveType] = useState<string>('ALL');
  const [activeCriticality, setActiveCriticality] = useState<string>('ALL');

  const fetchNotifications = async () => {
    try {
      const res = await operationsApi.module('notifications');
      const data = res as {
        items: OperationsNotification[];
        settings: {
          email_mirror_enabled: boolean;
          quiet_hours: string;
          delivery_mode: string;
          subscribed_tags: string[];
        };
      };
      setNotifications(data.items || []);
      if (data.settings) setSettings(data.settings);
    } catch {
      setNotifications([]);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await operationsApi.readAllNotifications();
      setNotifications(notifications.map((n) => ({ ...n, is_read: true })));
    } catch {
      alert('Ошибка при отметке всех уведомлений');
    }
  };

  const handleMarkRead = async (id: string, actionUrl?: string | null) => {
    try {
      await operationsApi.readNotification(id);
      setNotifications(notifications.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      if (actionUrl) {
        navigate(actionUrl);
      }
    } catch {
      if (actionUrl) navigate(actionUrl);
    }
  };

  const filteredNotifications = notifications.filter((item) => {
    const matchesType = activeType === 'ALL' || item.type === activeType;
    const matchesCrit = activeCriticality === 'ALL' || item.criticality === activeCriticality;
    return matchesType && matchesCrit;
  });

  const getCritBadge = (crit: string) => {
    switch (crit) {
      case 'URGENT':
        return { label: 'СРОЧНО', bg: '#dc2626', color: '#ffffff' };
      case 'HIGH':
        return { label: 'ВАЖНО', bg: '#d97706', color: '#ffffff' };
      default:
        return { label: 'ИНФО', bg: '#0284c7', color: '#ffffff' };
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            type="button"
            onClick={() => navigate('/app/operations')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              backgroundColor: '#1e293b',
              border: '1px solid #334155',
              color: '#94a3b8',
              cursor: 'pointer',
              fontSize: '1rem',
            }}
          >
            ←
          </button>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f8fafc', margin: 0 }}>
              Уведомления
            </h1>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.875rem' }}>
              Системные сигналы, изменения статусов заявок и напоминания о дедлайнах
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleMarkAllRead}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '6px',
            color: '#cbd5e1',
            fontSize: '0.8125rem',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          ✓ Отметить всё как прочитанное
        </button>
      </div>

      <div
        style={{
          backgroundColor: '#0f172a',
          border: '1px solid #1e293b',
          borderRadius: '10px',
          padding: '1.25rem',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
        }}
      >
        <div>
          <div style={{ fontSize: '0.8125rem', color: '#94a3b8', textTransform: 'uppercase' }}>
            Канал доставки
          </div>
          <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#f8fafc', marginTop: '2px' }}>
            {settings.delivery_mode} · Тихие часы: {settings.quiet_hours}
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {settings.subscribed_tags.map((tag) => (
            <span
              key={tag}
              style={{
                backgroundColor: '#1e293b',
                color: '#38bdf8',
                padding: '0.25rem 0.625rem',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                border: '1px solid #334155',
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.75rem',
          backgroundColor: '#0f172a',
          padding: '1rem',
          borderRadius: '10px',
          border: '1px solid #1e293b',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {[
            { id: 'ALL', label: 'Все события' },
            { id: 'REQUEST', label: 'Заявки' },
            { id: 'DEADLINE', label: 'Дедлайны' },
            { id: 'SYSTEM', label: 'Системные' },
            { id: 'MESSAGE', label: 'Сообщения' },
          ].map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActiveType(item.id)}
              style={{
                padding: '0.4rem 0.875rem',
                borderRadius: '6px',
                border: activeType === item.id ? '1px solid #38bdf8' : '1px solid #334155',
                backgroundColor: activeType === item.id ? '#0369a1' : '#1e293b',
                color: activeType === item.id ? '#ffffff' : '#94a3b8',
                fontSize: '0.8125rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {[
            { id: 'ALL', label: 'Любая важность' },
            { id: 'URGENT', label: 'Срочные' },
            { id: 'HIGH', label: 'Важные' },
            { id: 'INFO', label: 'Инфо' },
          ].map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActiveCriticality(item.id)}
              style={{
                padding: '0.4rem 0.875rem',
                borderRadius: '6px',
                border: activeCriticality === item.id ? '1px solid #fbbf24' : '1px solid #334155',
                backgroundColor: activeCriticality === item.id ? '#854d0e' : '#1e293b',
                color: activeCriticality === item.id ? '#ffffff' : '#94a3b8',
                fontSize: '0.8125rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {filteredNotifications.length === 0 ? (
          <div
            style={{
              padding: '3rem',
              textAlign: 'center',
              backgroundColor: '#0f172a',
              border: '1px solid #1e293b',
              borderRadius: '10px',
              color: '#64748b',
            }}
          >
            Уведомления отсутствуют
          </div>
        ) : (
          filteredNotifications.map((notif) => {
            const badge = getCritBadge(notif.criticality);
            return (
              <div
                key={notif.id}
                style={{
                  backgroundColor: notif.is_read ? '#0f172a' : '#1e293b',
                  border: '1px solid #334155',
                  borderLeft: notif.is_read ? '1px solid #334155' : '4px solid #38bdf8',
                  borderRadius: '10px',
                  padding: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '1rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '0.2rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.6875rem',
                      fontWeight: 700,
                      backgroundColor: badge.bg,
                      color: badge.color,
                      flexShrink: 0,
                      marginTop: '2px',
                    }}
                  >
                    {badge.label}
                  </span>
                  <div>
                    <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#f8fafc' }}>
                      {notif.title}
                    </div>
                    <div style={{ fontSize: '0.8125rem', color: '#cbd5e1', marginTop: '4px', lineHeight: 1.4 }}>
                      {notif.summary}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '6px' }}>
                      {notif.time_label} {notif.object_id && `· Объект: ${notif.object_id}`}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => handleMarkRead(notif.id, notif.action_url)}
                    style={{
                      padding: '0.4rem 0.875rem',
                      backgroundColor: '#0284c7',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    Перейти →
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
